/**
 * This page is opened in the external mobile browser by the native app.
 * It auto-triggers the Lovable managed OAuth flow for the given provider.
 *
 * Flow:
 * 1. Native app opens: https://translatify-chat.lovable.app/auth/start-oauth?provider=google
 * 2. This page loads and immediately calls lovable.auth.signInWithOAuth()
 * 3. The library redirects to the OAuth provider (Google/Apple)
 * 4. After auth, provider redirects back to /auth/native-callback
 * 5. NativeAuthCallback deep-links tokens back to the native app
 */
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { lovable } from '@/integrations/lovable/index';
import { Loader2 } from 'lucide-react';

const NativeOAuthStart = () => {
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const provider = searchParams.get('provider') as 'google' | 'apple' | null;

    if (!provider || !['google', 'apple'].includes(provider)) {
      setError('Invalid or missing provider');
      return;
    }

    const startOAuth = async () => {
      try {
        const callbackUrl = `${window.location.origin}/auth/native-callback`;
        console.log('[NativeOAuthStart] Starting OAuth for', provider, 'with callback:', callbackUrl);

        const { error: oauthError } = await lovable.auth.signInWithOAuth(provider, {
          redirect_uri: callbackUrl,
          extraParams: provider === 'google' ? { prompt: 'select_account' } : undefined,
        });

        if (oauthError) {
          console.error('[NativeOAuthStart] OAuth error:', oauthError);
          setError(oauthError.message || 'OAuth failed');
        }
        // If no error and result.redirected, the page will navigate away automatically
      } catch (err: any) {
        console.error('[NativeOAuthStart] Error:', err);
        setError(err.message || 'Something went wrong');
      }
    };

    startOAuth();
  }, [searchParams]);

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background gap-4 p-6 text-center">
        <p className="text-destructive font-medium">Sign in failed</p>
        <p className="text-muted-foreground text-sm">{error}</p>
        <p className="text-muted-foreground text-xs">Please close this window and try again in the app.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background gap-4">
      <Loader2 className="w-8 h-8 animate-spin text-primary" />
      <p className="text-muted-foreground">Connecting to sign in...</p>
    </div>
  );
};

export default NativeOAuthStart;
